import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { BASE_URL } from "../config";
import { AuditTrailForExport } from "../util/getAuditTrailData";

const baseUrl = BASE_URL + "user";

/** API `user_information` value — do not offer as trainee in Give Training. */
const USER_INFORMATION_SYSTEM_GENERATED = "SYSTEM_GENERATED_USER";

// Fetch Users API
export const fetchUsers = createAsyncThunk(
  "users/fetchUsers",
  async ({ limit, offset, searchQuery, moduleNames = [], withoutToken = false }) => {
    let url = `${baseUrl}${withoutToken ? "/get-users" : "/get-all"}`;

    // If searchQuery is provided and contains parameters, use it directly
    // Otherwise, build the URL with limit and offset
    if (searchQuery && searchQuery.trim() !== "") {
      // Parse the query string to check for module_type_permissions
      const params = new URLSearchParams(searchQuery);
      const searchType = params.get("search_type");

      // Check if search_type includes module_type_permissions_view or module_type_permissions_edit (could be comma-separated list)
      if (searchType && moduleNames.length > 0) {
        const searchTypes = searchType.split(",").map((s) => s.trim());
        // Support both old format (module_type_permissions) and new format (module_type_permissions_view, module_type_permissions_edit)
        if (
          searchTypes.includes("module_type_permissions") ||
          searchTypes.includes("module_type_permissions_view") ||
          searchTypes.includes("module_type_permissions_edit")
        ) {
          // Add "Not Assigned" if not already present
          const allModuleNames = new Set(["Not Assigned", ...moduleNames]);
          // Send all module names in comma-separated format with trailing comma
          const modulesParam = Array.from(allModuleNames).join(",") + ",";
          params.append("modules", modulesParam);
        }
      }

      // Convert back to query string
      url += `?${params.toString()}`;
    } else {
      // Fallback to just limit and offset if no search query
      url += `?limit=${limit}&offset=${offset}`;
    }

    const response = await axios.get(url);
    return {
      users:
        response.data.users.map((u) => ({
          ...u,
          name: u.first_name + " " + u.last_name,
        })) || 0,
      totalUsers:
        response.data.pagination?.total || response.data.users?.length || 0,
    };
  }
);

export const fetchApprovedUsers = createAsyncThunk(
  "users/fetchApprovedUsers",
  async ({ limit, offset }) => {
    const response = await axios.get(
      `${baseUrl}/get-all?limit=${limit}&offset=${offset}&filterKey=Approve&filterValue=true`
    );
    return {
      users: response.data.users || 0,
      totalUsers:
        response.data.pagination?.total || response.data.users?.length || 0,
    };
  }
);
export const fetchSubmittedUsers = createAsyncThunk(
  "users/fetchSubmittedUsers",
  async ({ limit, offset }) => {
    const response = await axios.get(
      `${baseUrl}/get-all?limit=${limit}&offset=${offset}&filterKey=Submit&filterValue=true`
    );
    return {
      users: response.data.users || 0,
      totalUsers:
        response.data.pagination?.total || response.data.users?.length || 0,
    };
  }
);

export const fetchReleasedUsers = createAsyncThunk(
  "users/fetchReleasedUsers",
  async ({ limit, offset }) => {
    const response = await axios.get(
      `${baseUrl}/get-all?limit=${limit}&offset=${offset}&filterKey=Release&filterValue=true`
    );
    return {
      users: response.data.users || 0,
      totalUsers:
        response.data.pagination?.total || response.data.users?.length || 0,
    };
  }
);

/**
 * Give Training → Select Trainee(s): GET user/get-all with part_ref (part id only) + sub_aprr_rel_users
 * (no filterKey/filterValue).
 */
export const fetchGiveTrainingTraineeUsers = createAsyncThunk(
  "users/fetchGiveTrainingTraineeUsers",
  async (
    {
      limit = 100,
      offset = 0,
      partRef,
      subAprRelUserIds = [],
    },
    { rejectWithValue },
  ) => {
    try {
      const params = new URLSearchParams();
      params.set("limit", String(limit));
      params.set("offset", String(offset));
      if (partRef != null && String(partRef).trim() !== "") {
        params.set("part_ref", String(partRef).trim());
      }
      const ids = Array.isArray(subAprRelUserIds)
        ? subAprRelUserIds
            .map((id) => String(id).trim())
            .filter(
              (s) =>
                s !== "" &&
                s !== "false" &&
                s !== "true" &&
                /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
                  s,
                ),
            )
        : [];
      params.set("sub_aprr_rel_users", JSON.stringify(ids));
      const response = await axios.get(`${baseUrl}/get-all?${params.toString()}`);
      const raw = response.data.users || [];
      const filtered = raw.filter(
        (u) => u?.user_information !== USER_INFORMATION_SYSTEM_GENERATED,
      );
      const users = filtered.map((u) => ({
        ...u,
        name: `${u.first_name || ""} ${u.last_name || ""}`.trim(),
      }));
      return {
        users,
        totalUsers: users.length,
      };
    } catch (error) {
      return rejectWithValue(
        error.response?.data || error.message || "Error loading trainee users",
      );
    }
  },
);

/**
 * Get Training → Select the Trainer: GET user/get-all with part_ref + sub_aprr_rel_users
 * (trainer ids from training-log rows where part_number is null). No filterKey/filterValue.
 */
export const fetchSelectTrainerUsers = createAsyncThunk(
  "users/fetchSelectTrainerUsers",
  async (
    {
      limit = 100,
      offset = 0,
      partRef,
      subAprRelUserIds = [],
    },
    { rejectWithValue },
  ) => {
    try {
      const params = new URLSearchParams();
      params.set("limit", String(limit));
      params.set("offset", String(offset));
      if (partRef != null && String(partRef).trim() !== "") {
        params.set("part_ref", String(partRef).trim());
      }
      const ids = Array.isArray(subAprRelUserIds)
        ? subAprRelUserIds
            .map((id) => String(id).trim())
            .filter(
              (s) =>
                s !== "" &&
                s !== "false" &&
                s !== "true" &&
                /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
                  s,
                ),
            )
        : [];
      params.set("sub_aprr_rel_users", JSON.stringify(ids));
      const response = await axios.get(`${baseUrl}/get-all?${params.toString()}`);
      const raw = response.data.users || [];
      const users = raw.map((u) => ({
        ...u,
        name: `${u.first_name || ""} ${u.last_name || ""}`.trim(),
      }));
      return {
        users,
        totalUsers:
          response.data.pagination?.total || users.length || 0,
      };
    } catch (error) {
      return rejectWithValue(
        error.response?.data || error.message || "Error loading trainer users",
      );
    }
  },
);

// Change User Status (disable user flow)
export const changeUserStatus = createAsyncThunk(
  "users/changeUserStatus",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${baseUrl}/change-user-status`,
        payload
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || "Error changing user status"
      );
    }
  }
);

// Delete User API
export const deleteUser = createAsyncThunk(
  "users/deleteUser",
  async (userId, { rejectWithValue }) => {
    try {
      const response = await axios.delete(
        `${baseUrl}/delete-account/${userId}`
      );

      // Check if the response indicates failure
      if (response.data?.success === false) {
        return rejectWithValue(response.data?.error || "Error deleting user");
      }

      return { userId, data: response.data };
    } catch (error) {
      // Handle axios errors
      const errorMessage =
        error.response?.data?.error ||
        error.response?.data?.message ||
        error.message ||
        "Error deleting user";
      return rejectWithValue(errorMessage);
    }
  }
);

// Create or Edit User API (Do NOT remove)
export const saveUser = createAsyncThunk("users/saveUser", async (userData) => {
  const url = userData.id
    ? `${baseUrl}/${userData.id}` // Edit API
    : `${baseUrl}/users`; // Create API

  const method = userData.id ? "put" : "post";

  const response = await axios({
    method,
    url,
    data: userData,
  });

  return response.data;
});

// Export Users CSV API
export const exportUsersCSV = createAsyncThunk(
  "users/exportCSV",
  async (moduleNames = [], { rejectWithValue }) => {
    try {
      // Prepare modules array with "Not Assigned" included
      const allModuleNames = ["Not Assigned", ...moduleNames];

      await AuditTrailForExport({
        apiUrl: `${baseUrl}/export`,
        file_name: "export-user-" + Date.now() + ".csv",
        action: "EXPORT_Audit_data",
        action_to_show: "Users data exported as CSV",
        additionalData: {
          modules: allModuleNames,
        },
      });
      return [];
    } catch (error) {
      return rejectWithValue(
        error.response?.data || "Error exporting users CSV"
      );
    }
  }
);

const userSlice = createSlice({
  name: "users",
  initialState: {
    users: [],
    totalUsers: 0,
    loading: false,
    error: null,
    approvedUsers: [],
    releasedUsers: [],
    submittedUsers: [],
    /** Give Training → Select Trainee(s) — GET user/get-all: part_ref = part id, sub_aprr_rel_users */
    giveTrainingTraineeUsers: [],
    giveTrainingTraineeUsersLoading: false,
    /** Get Training → Select the Trainer — GET user/get-all: part_ref + sub_aprr_rel_users (no Submit filter) */
    selectTrainerUsers: [],
    selectTrainerUsersLoading: false,
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearGiveTrainingTraineeUsers: (state) => {
      state.giveTrainingTraineeUsers = [];
      state.giveTrainingTraineeUsersLoading = false;
    },
    clearSelectTrainerUsers: (state) => {
      state.selectTrainerUsers = [];
      state.selectTrainerUsersLoading = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUsers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUsers.fulfilled, (state, action) => {
        state.loading = false;
        state.users = action.payload.users; // API should return `users` array
        state.totalUsers = action.payload.totalUsers; // API should return total user count
      })
      .addCase(fetchUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(fetchReleasedUsers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchReleasedUsers.fulfilled, (state, action) => {
        state.loading = false;
        state.releasedUsers = action.payload.users; // API should return `users` array
        state.totalUsers = action.payload.totalUsers; // API should return total user count
      })
      .addCase(fetchReleasedUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(fetchSubmittedUsers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSubmittedUsers.fulfilled, (state, action) => {
        state.loading = false;
        state.submittedUsers = action.payload.users; // API should return `users` array
        state.totalUsers = action.payload.totalUsers; // API should return total user count
      })
      .addCase(fetchSubmittedUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(fetchApprovedUsers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchApprovedUsers.fulfilled, (state, action) => {
        state.loading = false;
        state.approvedUsers = action.payload.users; // API should return `users` array
        state.totalUsers = action.payload.totalUsers; // API should return total user count
      })
      .addCase(fetchApprovedUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(fetchGiveTrainingTraineeUsers.pending, (state) => {
        state.giveTrainingTraineeUsersLoading = true;
      })
      .addCase(fetchGiveTrainingTraineeUsers.fulfilled, (state, action) => {
        state.giveTrainingTraineeUsersLoading = false;
        state.giveTrainingTraineeUsers = action.payload.users || [];
      })
      .addCase(fetchGiveTrainingTraineeUsers.rejected, (state) => {
        state.giveTrainingTraineeUsersLoading = false;
        state.giveTrainingTraineeUsers = [];
      })
      .addCase(fetchSelectTrainerUsers.pending, (state) => {
        state.selectTrainerUsersLoading = true;
      })
      .addCase(fetchSelectTrainerUsers.fulfilled, (state, action) => {
        state.selectTrainerUsersLoading = false;
        state.selectTrainerUsers = action.payload.users || [];
      })
      .addCase(fetchSelectTrainerUsers.rejected, (state) => {
        state.selectTrainerUsersLoading = false;
        state.selectTrainerUsers = [];
      })
      .addCase(changeUserStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(changeUserStatus.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(changeUserStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || "Error changing user status";
      })
      .addCase(saveUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(saveUser.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(saveUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(deleteUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteUser.fulfilled, (state, action) => {
        state.loading = false;
        // Remove the deleted user from the users array
        state.users = state.users.filter(
          (user) =>
            user.id !== action.payload.userId &&
            user.user_id !== action.payload.userId
        );
        state.totalUsers = Math.max(0, state.totalUsers - 1);
      })
      .addCase(deleteUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Error deleting user";
      })
      .addCase(exportUsersCSV.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(exportUsersCSV.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(exportUsersCSV.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Error exporting users CSV";
      });
  },
});

export const {
  clearError,
  clearGiveTrainingTraineeUsers,
  clearSelectTrainerUsers,
} = userSlice.actions;
export default userSlice.reducer;
